import React, { useEffect, useState, useRef } from "react";import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  Loader2,
  Upload,
  Check,
  Image as ImageIcon,
  RefreshCw,
  Users,
  CalendarCheck,
  Truck,
  LayoutDashboard,
  Image as ImgIcon,
  TrendingUp,
  BadgeIndianRupee,
  MapPin,
  Search,
  Trash2,
  KeyRound,
  ChevronDown,
} from "lucide-react";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const CATEGORY_LABEL = {
  "electric-bicycle": "Electric Bicycles",
  "low-speed-scooter": "Low-Speed Scooters",
  "high-speed-scooter": "High-Speed Scooters",
  "electric-motorcycle": "Electric Motorcycles",
};

const fmtINR = (v) => `₹${(v || 0).toLocaleString("en-IN")}`;
const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
  } catch (e) {
    return iso;
  }
};

/**
 * Confirm-then-soft-delete button. Shows an AlertDialog with the entity label,
 * calls the caller-supplied async `onConfirm` and lets the parent re-fetch.
 */
const SoftDeleteButton = ({ label, description, onConfirm, testId, disabled = false, disabledReason }) => {
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);

  const handle = async () => {
    setBusy(true);
    try {
      await onConfirm();
      setOpen(false);
    } finally {
      setBusy(false);
    }
  };

  if (disabled) {
    return (
      <button
        type="button"
        disabled
        title={disabledReason}
        data-testid={testId}
        className="w-8 h-8 rounded-lg text-gray-300 flex items-center justify-center cursor-not-allowed"
        aria-label="Delete disabled"
      >
        <Trash2 size={14} />
      </button>
    );
  }

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger asChild>
        <button
          type="button"
          data-testid={testId}
          className="w-8 h-8 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 flex items-center justify-center transition-colors"
          aria-label={`Delete ${label}`}
        >
          <Trash2 size={14} />
        </button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete {label}?</AlertDialogTitle>
          <AlertDialogDescription>
            {description ||
              `${label} will be soft-deleted. The record stays in the database with an audit trail but is hidden across the app. This cannot be undone from the UI.`}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid={`${testId}-cancel`} disabled={busy}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            data-testid={`${testId}-confirm`}
            onClick={(e) => { e.preventDefault(); handle(); }}
            disabled={busy}
            className="bg-red-600 hover:bg-red-700 text-white"
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : "Delete"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

/* ---------------- Overview / Analytics ---------------- */

const StatCard = ({ icon: Icon, label, value, accent, sub, testId }) => (
  <div className="card-premium p-6" data-testid={testId}>
    <div className="flex items-center gap-3">
      <div className={`w-10 h-10 rounded-xl ${accent} flex items-center justify-center text-white`}>
        <Icon size={18} />
      </div>
      <p className="text-[10px] uppercase tracking-widest font-bold text-gray-500">{label}</p>
    </div>
    <p className="font-display font-extrabold text-3xl md:text-4xl mt-4 tracking-tight text-ink">{value}</p>
    {sub ? <p className="text-xs text-gray-500 mt-1">{sub}</p> : null}
  </div>
);

const Analytics = ({ stats }) => (
  <div className="space-y-8" data-testid="admin-analytics">
    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
      <StatCard testId="stat-users" icon={Users} accent="bg-[#2D7FF9]" label="Users" value={stats.users} />
      <StatCard testId="stat-bookings" icon={CalendarCheck} accent="bg-[#3DDC97]" label="Bookings" value={stats.bookings.total} sub={`${stats.bookings.active} active · ${stats.bookings.cancelled} cancelled`} />
      <StatCard testId="stat-revenue" icon={BadgeIndianRupee} accent="bg-brand-gradient" label="Revenue" value={fmtINR(stats.revenue)} sub="excludes cancellations" />
      <StatCard testId="stat-enquiries" icon={Truck} accent="bg-[#0A0B10]" label="Fleet enquiries" value={stats.fleet_enquiries} />
    </div>

    <div className="grid md:grid-cols-2 gap-4">
      <div className="card-premium p-6">
        <div className="flex items-center gap-2 mb-4">
          <TrendingUp size={16} className="text-[#2D7FF9]" />
          <p className="text-[11px] uppercase tracking-widest font-bold text-gray-500">Bookings by category</p>
        </div>
        {stats.by_category.length ? (
          <ul className="space-y-3">
            {stats.by_category.map((r) => (
              <li key={r.category} className="flex items-center justify-between text-sm">
                <span className="text-ink capitalize">{CATEGORY_LABEL[r.category] || r.category.replace(/-/g, " ")}</span>
                <span className="font-mono-num font-bold">{r.count}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-gray-500">No bookings yet.</p>
        )}
      </div>
      <div className="card-premium p-6">
        <div className="flex items-center gap-2 mb-4">
          <MapPin size={16} className="text-[#3DDC97]" />
          <p className="text-[11px] uppercase tracking-widest font-bold text-gray-500">Bookings by city</p>
        </div>
        {stats.by_city.length ? (
          <ul className="space-y-3">
            {stats.by_city.map((r) => (
              <li key={r.city} className="flex items-center justify-between text-sm">
                <span className="text-ink capitalize">{r.city}</span>
                <span className="font-mono-num font-bold">{r.count}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-gray-500">No bookings yet.</p>
        )}
      </div>
    </div>

    <ChangePasswordCard />
  </div>
);

/* ---------------- Change password (self-service) ---------------- */

const ChangePasswordCard = () => {
  const [form, setForm] = useState({ current: "", next: "", confirm: "" });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (form.next !== form.confirm) {
      toast.error("New password and confirmation don't match");
      return;
    }
    if (form.next.length < 6) {
      toast.error("New password must be at least 6 characters");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/change-password", {
        current_password: form.current,
        new_password: form.next,
      });
      toast.success("Password updated. Other devices have been signed out.");
      setForm({ current: "", next: "", confirm: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update password");
    }
    setLoading(false);
  };

  return (
    <div className="card-premium p-6" data-testid="admin-change-password-card">
      <div className="flex items-center gap-2 mb-1">
        <KeyRound size={16} className="text-[#2D7FF9]" />
        <p className="text-[11px] uppercase tracking-widest font-bold text-gray-500">Security</p>
      </div>
      <h3 className="font-display font-bold text-xl mt-1">Change your password</h3>
      <p className="text-sm text-gray-500 mt-1">
        Rotate your admin password anytime. All other sessions for your account will be signed out; you stay signed in on this device.
      </p>
      <form onSubmit={submit} className="mt-5 grid md:grid-cols-3 gap-3">
        <input
          type="password"
          required
          value={form.current}
          onChange={set("current")}
          placeholder="Current password"
          data-testid="cp-current-input"
          className="h-11 rounded-xl border border-gray-200 px-4 text-sm focus:outline-none focus:border-[#2D7FF9]"
        />
        <input
          type="password"
          required
          minLength={6}
          value={form.next}
          onChange={set("next")}
          placeholder="New password"
          data-testid="cp-new-input"
          className="h-11 rounded-xl border border-gray-200 px-4 text-sm focus:outline-none focus:border-[#2D7FF9]"
        />
        <input
          type="password"
          required
          minLength={6}
          value={form.confirm}
          onChange={set("confirm")}
          placeholder="Confirm new password"
          data-testid="cp-confirm-input"
          className="h-11 rounded-xl border border-gray-200 px-4 text-sm focus:outline-none focus:border-[#2D7FF9]"
        />
        <button
          type="submit"
          disabled={loading}
          data-testid="cp-submit-btn"
          className="btn-primary !py-2.5 !px-5 !text-sm md:col-span-3 justify-self-start"
        >
          {loading ? <Loader2 size={14} className="animate-spin" /> : "Update password"}
        </button>
      </form>
    </div>
  );
};

/* ---------------- Users table ---------------- */

const UsersTab = ({ users, currentUserId, onDelete }) => {
  const [q, setQ] = useState("");
  const filtered = users.filter((u) => {
    if (!q) return true;
    const needle = q.toLowerCase();
    return (
      (u.name || "").toLowerCase().includes(needle) ||
      (u.email || "").toLowerCase().includes(needle) ||
      (u.mobile || "").toLowerCase().includes(needle)
    );
  });

  return (
    <div className="card-premium p-6" data-testid="admin-users-tab">
      <div className="flex items-center justify-between mb-5 gap-4">
        <div>
          <p className="text-[10px] uppercase tracking-widest font-bold text-gray-500">All signed-up users</p>
          <h3 className="font-display font-bold text-xl mt-1">{filtered.length} of {users.length}</h3>
        </div>
        <div className="relative w-full max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={14} />
          <Input
            data-testid="admin-users-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name, email or mobile…"
            className="pl-9 h-10 rounded-xl text-sm"
          />
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[10px] uppercase tracking-widest text-gray-500 border-b border-gray-200">
              <th className="py-2.5 text-left font-semibold">Name</th>
              <th className="py-2.5 text-left font-semibold">Email</th>
              <th className="py-2.5 text-left font-semibold">Mobile</th>
              <th className="py-2.5 text-left font-semibold">Role</th>
              <th className="py-2.5 text-left font-semibold">Joined</th>
              <th className="py-2.5 text-right font-semibold w-16">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((u) => {
              const isSelf = u.id === currentUserId;
              const isAdminUser = u.role === "admin";
              const disabled = isSelf || isAdminUser;
              return (
                <tr key={u.id} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="py-3 font-medium text-ink">{u.name || "—"}</td>
                  <td className="py-3 text-gray-600 break-all">{u.email || "—"}</td>
                  <td className="py-3 text-gray-600 font-mono-num">{u.mobile || "—"}</td>
                  <td className="py-3">
                    <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full font-bold ${u.role === "admin" ? "bg-[#0A0B10] text-white" : "bg-[#3DDC97]/15 text-[#0d8f5b]"}`}>
                      {u.role || "user"}
                    </span>
                  </td>
                  <td className="py-3 text-gray-500 text-xs">{fmtDate(u.created_at)}</td>
                  <td className="py-3 text-right">
                    <SoftDeleteButton
                      label={`user ${u.name || u.email || u.mobile}`}
                      description={`This will soft-delete ${u.name || u.email}. Their sessions will be revoked and they will no longer be able to log in.`}
                      onConfirm={() => onDelete(u.id)}
                      testId={`delete-user-${u.id}`}
                      disabled={disabled}
                      disabledReason={isSelf ? "You cannot delete your own account" : "Cannot delete another admin"}
                    />
                  </td>
                </tr>
              );
            })}
            {!filtered.length && (
              <tr>
                <td colSpan={6} className="py-10 text-center text-sm text-gray-500">No users found.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

/* ---------------- Bookings table ---------------- */

const BookingsTab = ({ bookings, onDelete }) => (
  <div className="card-premium p-6" data-testid="admin-bookings-tab">
    <div className="mb-5">
      <p className="text-[10px] uppercase tracking-widest font-bold text-gray-500">All bookings</p>
      <h3 className="font-display font-bold text-xl mt-1">{bookings.length} total</h3>
    </div>
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-[10px] uppercase tracking-widest text-gray-500 border-b border-gray-200">
            <th className="py-2.5 text-left font-semibold">Rider</th>
            <th className="py-2.5 text-left font-semibold">Bike</th>
            <th className="py-2.5 text-left font-semibold">City</th>
            <th className="py-2.5 text-left font-semibold">Plan</th>
            <th className="py-2.5 text-left font-semibold">Total</th>
            <th className="py-2.5 text-left font-semibold">Status</th>
            <th className="py-2.5 text-left font-semibold">Created</th>
            <th className="py-2.5 text-right font-semibold w-16">Actions</th>
          </tr>
        </thead>
        <tbody>
          {bookings.map((b) => {
            const snap = b.bike_snapshot || {};
            const statusColor =
              b.status === "confirmed" ? "bg-[#3DDC97]/15 text-[#0d8f5b]" :
              b.status === "cancelled" ? "bg-red-100 text-red-700" :
              "bg-gray-100 text-gray-700";
            return (
              <tr key={b.id} className="border-b border-gray-100 hover:bg-gray-50">
                <td className="py-3">
                  <p className="font-medium text-ink">{b.user?.name || "—"}</p>
                  <p className="text-[11px] text-gray-500">{b.user?.email || b.user?.mobile || "—"}</p>
                </td>
                <td className="py-3">
                  <p className="font-medium text-ink">{snap.brand} {snap.name}</p>
                  <p className="text-[11px] text-gray-500 capitalize">{(snap.category || "").replace(/-/g, " ")}</p>
                </td>
                <td className="py-3 text-gray-600 capitalize">{b.city}</td>
                <td className="py-3 text-gray-600 capitalize">{b.plan}</td>
                <td className="py-3 font-mono-num font-bold">{fmtINR(b.total)}</td>
                <td className="py-3">
                  <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full font-bold ${statusColor}`}>
                    {b.status}
                  </span>
                </td>
                <td className="py-3 text-gray-500 text-xs">{fmtDate(b.created_at)}</td>
                <td className="py-3 text-right">
                  <SoftDeleteButton
                    label={`booking ${snap.brand || ""} ${snap.name || ""}`.trim()}
                    onConfirm={() => onDelete(b.id)}
                    testId={`delete-booking-${b.id}`}
                  />
                </td>
              </tr>
            );
          })}
          {!bookings.length && (
            <tr>
              <td colSpan={8} className="py-10 text-center text-sm text-gray-500">No bookings yet.</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  </div>
);

/* ---------------- Fleet enquiries ---------------- */

const EnquiriesTab = ({ enquiries, onDelete }) => (
  <div className="card-premium p-6" data-testid="admin-enquiries-tab">
    <div className="mb-5">
      <p className="text-[10px] uppercase tracking-widest font-bold text-gray-500">Fleet enquiries</p>
      <h3 className="font-display font-bold text-xl mt-1">{enquiries.length} received</h3>
    </div>
    <div className="grid gap-3">
      {enquiries.map((e) => (
        <div key={e.id} className="border border-gray-200 rounded-2xl p-5 hover:border-[#2D7FF9]/50 transition-colors" data-testid={`enquiry-${e.id}`}>
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <h4 className="font-display font-bold text-lg text-ink">{e.name}{e.company ? ` · ${e.company}` : ""}</h4>
              <p className="text-sm text-gray-600 mt-1">
                <a href={`mailto:${e.email}`} className="hover:text-[#2D7FF9]">{e.email}</a>
                {" · "}
                <a href={`tel:${e.mobile}`} className="hover:text-[#2D7FF9] font-mono-num">{e.mobile}</a>
              </p>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full font-bold bg-[#2D7FF9]/10 text-[#2D7FF9]">
                {e.status || "new"}
              </span>
              <SoftDeleteButton
                label={`enquiry from ${e.name}`}
                onConfirm={() => onDelete(e.id)}
                testId={`delete-enquiry-${e.id}`}
              />
            </div>
          </div>
          <div className="mt-3 grid sm:grid-cols-3 gap-2 text-xs">
            <div>
              <p className="text-[10px] uppercase tracking-widest text-gray-500">City</p>
              <p className="font-medium text-ink mt-0.5 capitalize">{e.city || "—"}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-widest text-gray-500">Fleet size</p>
              <p className="font-medium text-ink mt-0.5">{e.fleet_size || "—"}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-widest text-gray-500">Use case</p>
              <p className="font-medium text-ink mt-0.5">{e.use_case || "—"}</p>
            </div>
          </div>
          {e.message ? (
            <p className="mt-3 text-sm text-gray-700 leading-relaxed bg-gray-50 rounded-lg p-3">{e.message}</p>
          ) : null}
          <p className="text-[11px] text-gray-400 mt-3">{fmtDate(e.created_at)}</p>
        </div>
      ))}
      {!enquiries.length && (
        <div className="py-10 text-center text-sm text-gray-500">No fleet enquiries yet.</div>
      )}
    </div>
  </div>
);

/* ---------------- EV image manager (existing) ---------------- */

const ModelRow = ({ m, onUpdated }) => {
  const [uploading, setUploading] = useState(false);
  const [galleryUploading, setGalleryUploading] = useState(false);
  const [gallery, setGallery] = useState([]);
  const [expanded, setExpanded] = useState(false);
  const fileRef = useRef(null);
  const galleryRef = useRef(null);

  const key = `${m.brand}-${m.name}`.replace(/\s/g, "-");

  const loadGallery = async () => {
    try {
      const { data } = await api.get("/bikes/models/gallery", {
        params: { brand: m.brand, name: m.name },
      });
      setGallery(data.images || []);
    } catch (_) {
      setGallery([]);
    }
  };

  // Fetch gallery lazily when the row expands
  useEffect(() => {
    if (expanded && gallery.length === 0) loadGallery();
    // eslint-disable-next-line
  }, [expanded]);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.patch("/bikes/model/image", {
        brand: m.brand,
        name: m.name,
        image_url: data.url,
      });
      toast.success(`Updated hero image for ${m.brand} ${m.name}`);
      onUpdated();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  const handleGalleryFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setGalleryUploading(true);
    let last = null;
    for (const file of files) {
      try {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        const { data: after } = await api.post("/bikes/model/gallery", {
          brand: m.brand,
          name: m.name,
          image_url: data.url,
        });
        last = after.images;
      } catch (err) {
        toast.error(err?.response?.data?.detail || `Failed to upload ${file.name}`);
      }
    }
    if (last) setGallery(last);
    toast.success(`Added ${files.length} image${files.length === 1 ? "" : "s"} to gallery`);
    setGalleryUploading(false);
    if (galleryRef.current) galleryRef.current.value = "";
  };

  const removeGalleryImage = async (url) => {
    try {
      const { data } = await api.delete("/bikes/model/gallery", {
        data: { brand: m.brand, name: m.name, image_url: url },
      });
      setGallery(data.images || []);
      toast.success("Image removed from gallery");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  /* --- Drag-reorder support --- */
  const [dragIdx, setDragIdx] = useState(null);
  const [dragOverIdx, setDragOverIdx] = useState(null);

  const persistOrder = async (ordered) => {
    try {
      const { data } = await api.put("/bikes/model/gallery/reorder", {
        brand: m.brand,
        name: m.name,
        images: ordered,
      });
      setGallery(data.images || ordered);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reorder failed");
      // Roll back to server truth
      loadGallery();
    }
  };

  const handleDragStart = (i) => (e) => {
    setDragIdx(i);
    e.dataTransfer.effectAllowed = "move";
    // Set data for firefox
    e.dataTransfer.setData("text/plain", String(i));
  };

  const handleDragOver = (i) => (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (dragOverIdx !== i) setDragOverIdx(i);
  };

  const handleDrop = (i) => (e) => {
    e.preventDefault();
    const from = dragIdx;
    setDragIdx(null);
    setDragOverIdx(null);
    if (from === null || from === i) return;
    const next = [...gallery];
    const [moved] = next.splice(from, 1);
    next.splice(i, 0, moved);
    setGallery(next);
    persistOrder(next);
  };

  const handleDragEnd = () => {
    setDragIdx(null);
    setDragOverIdx(null);
  };

  return (
    <div className="card-premium p-5" data-testid={`model-row-${key}`}>
      <div className="flex gap-5 items-center">
        <div className="w-24 h-24 rounded-xl overflow-hidden bg-gray-100 shrink-0">
          {m.image ? <img src={m.image} alt={m.name} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-gray-400"><ImageIcon size={22} /></div>}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] uppercase tracking-widest text-[#2D7FF9] font-bold">{CATEGORY_LABEL[m.category] || m.category}</p>
          <h3 className="font-display font-bold text-lg mt-0.5 truncate">{m.brand} {m.name}</h3>
          <p className="text-xs text-gray-500 mt-1">Deployed across {m.count} {m.count === 1 ? "city" : "cities"}</p>
        </div>
        <input ref={fileRef} type="file" accept="image/*" onChange={handleFile} className="hidden" data-testid={`file-input-${key}`} />
        <button
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          data-testid={`upload-btn-${key}`}
          className="btn-primary !py-2.5 !px-4 !text-sm shrink-0 disabled:opacity-50"
        >
          {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
          <span>{uploading ? "Uploading…" : "Hero image"}</span>
        </button>
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          data-testid={`gallery-toggle-${key}`}
          className="text-xs font-semibold text-gray-600 hover:text-ink flex items-center gap-1 shrink-0 px-3 py-2 rounded-full border border-gray-200 hover:border-gray-300"
        >
          <ImageIcon size={13} />
          Gallery
          <ChevronDown
            size={13}
            className={`transition-transform ${expanded ? "rotate-180" : ""}`}
          />
        </button>
      </div>

      {expanded && (
        <div className="mt-5 pt-5 border-t border-gray-100" data-testid={`gallery-section-${key}`}>
          <div className="flex items-center justify-between mb-3">
            <div>
              <p className="text-[10px] uppercase tracking-widest text-gray-500 font-bold">Additional images</p>
              <p className="text-xs text-gray-500 mt-0.5">
                Shown as a thumbnail strip on the product detail page — the hero image above stays on the card.
              </p>
            </div>
            <input
              ref={galleryRef}
              type="file"
              accept="image/*"
              multiple
              onChange={handleGalleryFiles}
              className="hidden"
              data-testid={`gallery-input-${key}`}
            />
            <button
              onClick={() => galleryRef.current?.click()}
              disabled={galleryUploading}
              data-testid={`gallery-upload-${key}`}
              className="btn-primary !py-2 !px-4 !text-xs shrink-0 disabled:opacity-50"
            >
              {galleryUploading ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />}
              <span>{galleryUploading ? "Uploading…" : "Add images"}</span>
            </button>
          </div>
          {gallery.length === 0 ? (
            <div className="rounded-2xl border-2 border-dashed border-gray-200 py-8 text-center text-sm text-gray-500">
              No additional images yet. Click <span className="font-semibold text-ink">Add images</span> to upload one or more.
            </div>
          ) : (
            <>
              <p className="text-[11px] text-gray-500 mb-2">
                Drag & drop the thumbnails to reorder the sequence shown on the product page.
              </p>
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3">
                {gallery.map((url, i) => {
                  const isDragging = dragIdx === i;
                  const isTarget = dragOverIdx === i && dragIdx !== null && dragIdx !== i;
                  return (
                    <div
                      key={url}
                      draggable
                      onDragStart={handleDragStart(i)}
                      onDragOver={handleDragOver(i)}
                      onDrop={handleDrop(i)}
                      onDragEnd={handleDragEnd}
                      className={`relative group aspect-square rounded-xl overflow-hidden bg-gray-100 cursor-grab active:cursor-grabbing transition-all ${isDragging ? "opacity-40 scale-95" : ""} ${isTarget ? "ring-2 ring-[#3DDC97] ring-offset-2" : ""}`}
                      data-testid={`gallery-thumb-${key}-${i}`}
                    >
                      <img src={url} alt="" draggable={false} className="w-full h-full object-cover pointer-events-none" />
                      <span className="absolute top-1.5 left-1.5 text-[10px] font-mono-num font-bold bg-black/70 text-white px-1.5 py-0.5 rounded-md">
                        {i + 1}
                      </span>
                      <button
                        type="button"
                        onClick={() => removeGalleryImage(url)}
                        data-testid={`gallery-remove-${key}-${i}`}
                        className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-white"
                        aria-label="Remove image"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

const ImagesTab = ({ models, onRefresh }) => {
  const grouped = models.reduce((acc, m) => {
    (acc[m.category] = acc[m.category] || []).push(m);
    return acc;
  }, {});
  return (
    <div className="space-y-10" data-testid="admin-images-tab">
      <div className="card-premium p-6 border-dashed">
        <div className="flex items-start gap-3">
          <Check size={18} className="text-[#3DDC97] mt-0.5" />
          <div className="text-sm text-gray-600">
            <p className="font-semibold text-ink">Upload rules</p>
            <p className="mt-1">
              Max 8 MB per image. Accepted: JPG, PNG, WEBP, GIF. Uploads apply to every city that
              carries that EV. URLs are stored as relative paths so they survive redeploys.
            </p>
          </div>
        </div>
      </div>
      {Object.entries(grouped).map(([cat, list]) => (
        <div key={cat}>
          <div className="flex items-center gap-3 mb-4">
            <h2 className="font-display font-bold text-xl">{CATEGORY_LABEL[cat] || cat}</h2>
            <span className="text-xs px-2 py-0.5 rounded-full bg-[#3DDC97]/10 text-[#0d8f5b] font-semibold">{list.length} models</span>
          </div>
          <div className="space-y-3">
            {list.map((m) => (
              <ModelRow key={`${m.brand}-${m.name}`} m={m} onUpdated={onRefresh} />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

/* ---------------- Root ---------------- */

const Admin = () => {
  const { user } = useAuth();
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);

  const load = async () => {
    setLoading(true);
    try {
      const [s, u, b, e, m] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/users"),
        api.get("/admin/bookings"),
        api.get("/admin/enquiries/fleet"),
        api.get("/bikes/models/list"),
      ]);
      setStats(s.data);
      setUsers(u.data);
      setBookings(b.data);
      setEnquiries(e.data);
      setModels(m.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load admin data");
    }
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [refreshKey]);

  const deleteResource = (path, label) => async (id) => {
    try {
      await api.delete(`${path}/${id}`);
      toast.success(`${label} deleted`);
      setRefreshKey((k) => k + 1);
    } catch (err) {
      toast.error(err?.response?.data?.detail || `Could not delete ${label.toLowerCase()}`);
    }
  };

  const deleteUser = deleteResource("/admin/users", "User");
  const deleteBooking = deleteResource("/admin/bookings", "Booking");
  const deleteEnquiry = deleteResource("/admin/enquiries/fleet", "Enquiry");

  return (
    <div className="min-h-screen bg-[#F8F9FA]" data-testid="admin-page">
      <Navbar />
      <div className="max-w-7xl mx-auto px-5 md:px-8 py-12">
        <div className="flex items-start justify-between gap-4 mb-8 flex-wrap">
          <div>
            <p className="text-[10px] uppercase tracking-widest text-[#2D7FF9] font-bold font-mono-num">— ADMIN CONSOLE</p>
            <h1 className="font-display font-extrabold text-4xl md:text-5xl mt-3 tracking-tight">Operations dashboard</h1>
            <p className="text-gray-600 mt-2">Welcome, {user?.name}. Monitor riders, bookings, fleet leads and manage the catalogue.</p>
          </div>
          <button onClick={() => setRefreshKey((k) => k + 1)} data-testid="admin-refresh-btn" className="flex items-center gap-2 text-sm font-semibold text-gray-600 hover:text-ink">
            <RefreshCw size={14} /> Refresh
          </button>
        </div>

        {loading || !stats ? (
          <div className="flex justify-center py-16"><Loader2 className="animate-spin text-gray-400" /></div>
        ) : (
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="grid grid-cols-2 md:grid-cols-5 w-full bg-white border border-gray-200 rounded-2xl p-1 h-auto mb-8">
              <TabsTrigger value="overview" data-testid="admin-tab-overview" className="rounded-xl data-[state=active]:bg-[#0A0B10] data-[state=active]:text-white py-2.5 gap-2">
                <LayoutDashboard size={14} /> Overview
              </TabsTrigger>
              <TabsTrigger value="users" data-testid="admin-tab-users" className="rounded-xl data-[state=active]:bg-[#0A0B10] data-[state=active]:text-white py-2.5 gap-2">
                <Users size={14} /> Users
              </TabsTrigger>
              <TabsTrigger value="bookings" data-testid="admin-tab-bookings" className="rounded-xl data-[state=active]:bg-[#0A0B10] data-[state=active]:text-white py-2.5 gap-2">
                <CalendarCheck size={14} /> Bookings
              </TabsTrigger>
              <TabsTrigger value="enquiries" data-testid="admin-tab-enquiries" className="rounded-xl data-[state=active]:bg-[#0A0B10] data-[state=active]:text-white py-2.5 gap-2">
                <Truck size={14} /> Enquiries
              </TabsTrigger>
              <TabsTrigger value="images" data-testid="admin-tab-images" className="rounded-xl data-[state=active]:bg-[#0A0B10] data-[state=active]:text-white py-2.5 gap-2">
                <ImgIcon size={14} /> EV Images
              </TabsTrigger>
            </TabsList>

            <TabsContent value="overview" className="m-0"><Analytics stats={stats} /></TabsContent>
            <TabsContent value="users" className="m-0"><UsersTab users={users} currentUserId={user?.id} onDelete={deleteUser} /></TabsContent>
            <TabsContent value="bookings" className="m-0"><BookingsTab bookings={bookings} onDelete={deleteBooking} /></TabsContent>
            <TabsContent value="enquiries" className="m-0"><EnquiriesTab enquiries={enquiries} onDelete={deleteEnquiry} /></TabsContent>
            <TabsContent value="images" className="m-0"><ImagesTab models={models} onRefresh={() => setRefreshKey((k) => k + 1)} /></TabsContent>
          </Tabs>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default Admin;
